import { useCallback, useState } from "react";
import { message } from "antd";
import { ADMIN_EVENTS } from "../../../constants/api";
import { eventApi } from "./utils/eventApi";
import { logEventDebug, showEventApiError } from "./utils/eventMessages";
import {
  formatDisplayDate,
  formatParticipants,
  getEventBackendType,
  getEventExpirationDateApiValue,
  getEventStatus,
  normalizeEventType,
} from "./utils/eventFormatters";

const getEventsPayload = (payload) => {
  if (Array.isArray(payload)) {
    return payload;
  }

  if (Array.isArray(payload?.events)) {
    return payload.events;
  }

  if (Array.isArray(payload?.items)) {
    return payload.items;
  }

  if (Array.isArray(payload?.data)) {
    return payload.data;
  }

  if (payload && typeof payload === "object") {
    return [payload];
  }

  return [];
};

const createDeleteEventPayload = (event) => ({
  type: getEventBackendType(event),
  expiration_date: getEventExpirationDateApiValue(event),
});

const normalizeEvent = (event, index) => {
  const eventData = event || {};
  const startDate = formatDisplayDate(
    eventData.start_date || eventData.startDate
  );
  const endDate = formatDisplayDate(
    eventData.expiration_date || eventData.end_date || eventData.endDate
  );
  const id =
    eventData.event_id ||
    eventData.eventId ||
    eventData.id ||
    `${eventData.type || "event"}-${eventData.expiration_date || index}`;

  return {
    id,
    displayId:
      eventData.displayId || eventData.event_id || eventData.id || index + 1,
    name: eventData.title || eventData.name || "",
    category: normalizeEventType(eventData.type || eventData.category),
    status: eventData.status || getEventStatus({ startDate, endDate }),
    startDate,
    endDate,
    participants: formatParticipants(
      eventData.participants ||
        eventData.participants_count ||
        eventData.total_participants ||
        eventData.totalParticipants ||
        eventData.metrics?.participations
    ),
    json: eventData.additional_attributes || eventData.json || null,
    originalEvent: eventData,
  };
};

const normalizeEventsResponse = (payload) =>
  getEventsPayload(payload).map((event, index) => normalizeEvent(event, index));

export const useEventActions = () => {
  const [eventsList, setEventsList] = useState([]);
  const [isLoadingEvents, setIsLoadingEvents] = useState(false);
  const [deletingEventId, setDeletingEventId] = useState(null);

  const getEvents = useCallback(async () => {
    try {
      setIsLoadingEvents(true);

      const response = await eventApi.get(ADMIN_EVENTS);

      logEventDebug("Eventos recebidos do backend:", response.data);
      setEventsList(normalizeEventsResponse(response.data));
    } catch (error) {
      logEventDebug("Erro ao buscar Eventos: ", error);
      setEventsList([]);
    } finally {
      setIsLoadingEvents(false);
    }
  }, []);

  const deleteEvent = useCallback(async (event) => {
    const deletePayload = createDeleteEventPayload(event);

    if (!deletePayload.type || !deletePayload.expiration_date) {
      message.error("Não foi possível identificar o evento para exclusão.");
      return false;
    }

    try {
      setDeletingEventId(event.id);

      await eventApi.delete(ADMIN_EVENTS, deletePayload);

      setEventsList((currentEvents) =>
        currentEvents.filter((currentEvent) => currentEvent.id !== event.id)
      );
      message.success("Evento deletado com sucesso.");
      return true;
    } catch (error) {
      showEventApiError(
        error,
        "Não foi possível deletar o evento.",
        "Erro ao deletar Evento: "
      );
      return false;
    } finally {
      setDeletingEventId(null);
    }
  }, []);

  return {
    getEvents,
    deleteEvent,
    eventsList,
    isLoadingEvents,
    deletingEventId,
  };
};
